import { useFragment, graphql } from 'react-relay';
import {
    hasRecommendationSnoozeExpired,
    getRecommendationSnoozeTime,
} from './recommendationsRedDot';

import { type useNotificationsConfig_seller$key } from './__generated__/useNotificationsConfig_seller.graphql';
import { type useNotificationsConfig_notifications$key } from './__generated__/useNotificationsConfig_notifications.graphql';
import { type useNotificationsConfig_viewer$key } from './__generated__/useNotificationsConfig_viewer.graphql';

type UseNotificationsConfigType = (args: {
    seller?: useNotificationsConfig_seller$key | null;
    notifications?: useNotificationsConfig_notifications$key | null;
    viewer?: useNotificationsConfig_viewer$key | null;
}) => Record<
    | 'ordersIcon'
    | 'messageIcon'
    | 'listingsIcon'
    | 'recommendationsIcon'
    | 'advertisingIcon'
    | 'statement'
    | 'finance',
    {
        count: [string, boolean];
        dataTn: string;
        showCount: boolean;
        isActionable?: boolean;
    } | null
>;

const sellerFragment = graphql`
    fragment useNotificationsConfig_seller on Seller {
        serviceId
        billingProfile {
            runningBalance
        }
    }
`;

const notificationsFragment = graphql`
    fragment useNotificationsConfig_notifications on DealerNavBarBadges {
        listings
        orders
        messages
        recommendations
    }
`;

const viewerFragment = graphql`
    fragment useNotificationsConfig_viewer on Viewer {
        onSessionSellerChargesEnabled: featureFlag(
            feature: "onSessionSellerBillingStatementCharges"
        )
    }
`;

const MAX_NOTIFICATIONS_COUNT = 50;

/**
 * returns tuple: [0] formatted count;
 * [1] boolean indicating if the notification
 * ceiling has been hit
 */
function formatCount(count: unknown): [string, boolean] {
    const countNumber = Number(count) || 0;

    if (countNumber > MAX_NOTIFICATIONS_COUNT) {
        return [`${MAX_NOTIFICATIONS_COUNT}+`, true];
    }
    return [countNumber.toString(), false];
}

export const useNotificationsConfig: UseNotificationsConfigType = ({
    seller: sellerRef,
    notifications: notificationsRef,
    viewer: viewerRef,
}) => {
    const seller = useFragment<useNotificationsConfig_seller$key>(sellerFragment, sellerRef);
    const notifications = useFragment<useNotificationsConfig_notifications$key>(
        notificationsFragment,
        notificationsRef
    );
    const viewer = useFragment<useNotificationsConfig_viewer$key>(
        viewerFragment,
        viewerRef || null
    );

    const { serviceId: sellerId } = seller || {};
    const { listings, orders, messages, recommendations } = notifications || {
        listings: null,
        orders: null,
        messages: null,
        recommendations: null,
    };

    const recommendationsSnoozeExpired = hasRecommendationSnoozeExpired(sellerId || '');
    const recommendationsSnoozedDate = getRecommendationSnoozeTime(sellerId || '');

    const statementBalance = seller?.billingProfile?.runningBalance || 0;
    const onSessionSellerChargesEnabled = viewer?.onSessionSellerChargesEnabled || false;

    return {
        statement: {
            count: formatCount(0),
            dataTn: 'statement',
            showCount: false,
            isActionable: statementBalance < 0 && onSessionSellerChargesEnabled,
        },
        finance: {
            count: formatCount(0),
            dataTn: 'finance',
            showCount: false,
            isActionable: statementBalance < 0 && onSessionSellerChargesEnabled,
        },
        ordersIcon: {
            count: formatCount(orders),
            dataTn: 'orders',
            showCount: true,
        },
        messageIcon: {
            count: formatCount(messages),
            dataTn: 'messages',
            showCount: true,
        },
        listingsIcon: {
            count: formatCount(listings),
            dataTn: 'listings',
            showCount: true,
        },
        recommendationsIcon: recommendationsSnoozeExpired
            ? {
                  count: formatCount(recommendations),
                  dataTn: 'recommendations',
                  showCount: false,
                  isSnoozed: !recommendationsSnoozeExpired,
                  snoozeDate: recommendationsSnoozedDate
                      ? new Date(recommendationsSnoozedDate)
                      : null,
              }
            : null,
        advertisingIcon: localStorage.getItem('SELLER_ADVERTISING_PAGE_SEEN')
            ? null
            : {
                  count: formatCount(1),
                  dataTn: 'advertising',
                  showCount: false,
              },
    };
};
